import {
  CommissionMonth,
  CommissionMonthWithProjected,
  CommissionStatus,
  GroupedCommissions,
  NestedCommission,
  TODO,
  YearAndMonth,
} from "@revelate/types";
import { deleteCommission, upsertCommission } from "../supabase/commissions";
import { updateDeal } from "../supabase/deals";
import { commissionCopy } from "./commission-access";
import {
  abbreviateLongString,
  compareYearMonth,
  yearMonthExistsInArray,
} from "@revelate/utils";
import { getCommissionsValue } from "@revelate/calc/commission";

export const groupCommissionsByMonth = (
  commissions: NestedCommission[]
): GroupedCommissions => {
  // First group by month-year
  const unordered = commissions.reduce(
    (acc: Record<string, NestedCommission[]>, commission) => {
      const key: string = `${commission.year}-${String(commission.month).padStart(2, "0")}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(commission);
      return acc;
    },
    {}
  );

  // Sort keys chronologically
  const orderedKeys = Object.keys(unordered).sort();

  // Create new ordered object
  return orderedKeys.reduce((acc: GroupedCommissions, key) => {
    acc[key] = unordered[key];
    return acc;
  }, {});
};

// Return type for groupCommissionsByDealName with display names
export type GroupedCommissionsWithDisplayNames = {
  commissionGroups: GroupedCommissions;
  displayNames: Record<string, string>;
  // Sorted entries to maintain order
  sortedEntries: [string, NestedCommission[]][];
};

export const groupCommissionsByDealName = (
  commissions: NestedCommission[]
): GroupedCommissions => {
  // Create a mapping of keys to display names to preserve deal names
  const keyToDisplayName: Record<string, string> = {};

  const result = commissions
    .toSorted((a, b) =>
      a.deal && b.deal && a.deal.closed_at && b.deal.closed_at
        ? (new Date(a.deal.closed_at) as TODO) -
        (new Date(b.deal.closed_at) as TODO)
        : a.deal && a.deal.closed_at
          ? -1
          : 1
    )
    .reduce((acc: TODO, commission) => {
      // Use deals_key as the unique identifier
      const key: string =
        commission.deals_key ||
        (commission.deal
          ? `${commission.deal.id}-${abbreviateLongString(commission?.deal?.name, 30)}`
          : commission?.plan?.name || "Additional commissions");

      // Store the display name for this key
      if (!keyToDisplayName[key] && commission.deal) {
        keyToDisplayName[key] = abbreviateLongString(commission.deal.name, 30);
      } else if (!keyToDisplayName[key]) {
        keyToDisplayName[key] =
          commission?.plan?.name || "Additional commissions";
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(commission);
      return acc;
    }, {});

  // Sort commissions within each deal by percentage (rate) in ascending order
  Object.keys(result).forEach((key) => {
    result[key] = result[key].sort(
      (a: NestedCommission, b: NestedCommission) =>
        (a.rate || 0) - (b.rate || 0)
    );
  });

  // For backward compatibility, just return the grouped commissions
  return result;
};

// New function that returns both the grouped commissions and display names
export const groupCommissionsByDealNameWithDisplayNames = (
  commissions: NestedCommission[]
): GroupedCommissionsWithDisplayNames => {
  // Create a mapping of keys to display names to preserve deal names
  const keyToDisplayName: Record<string, string> = {};
  // Track the earliest closing date for each key to maintain sorting
  const keyToEarliestClosingDate: Record<string, Date | null> = {};

  const result = commissions
    .toSorted((a, b) =>
      a.deal && b.deal && a.deal.closed_at && b.deal.closed_at
        ? (new Date(a.deal.closed_at) as TODO) -
        (new Date(b.deal.closed_at) as TODO)
        : a.deal && a.deal.closed_at
          ? -1
          : 1
    )
    .reduce((acc: TODO, commission) => {
      // Use deals_key as the unique identifier
      const key: string =
        commission.deals_key ||
        (commission.deal
          ? `${commission.deal.id}-${abbreviateLongString(commission?.deal?.name, 30)}`
          : commission?.plan?.name || "Additional commissions");

      // Store the display name for this key
      if (!keyToDisplayName[key] && commission.deal) {
        keyToDisplayName[key] = abbreviateLongString(commission.deal.name, 30);
      } else if (!keyToDisplayName[key]) {
        keyToDisplayName[key] =
          commission?.plan?.name || "Additional commissions";
      }

      // Track the earliest closing date for this key
      if (commission.deal?.closed_at) {
        const closingDate = new Date(commission.deal.closed_at);
        if (
          !keyToEarliestClosingDate[key] ||
          closingDate < keyToEarliestClosingDate[key]!
        ) {
          keyToEarliestClosingDate[key] = closingDate;
        }
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(commission);
      return acc;
    }, {});

  // Sort commissions within each deal by percentage (rate) in ascending order
  Object.keys(result).forEach((key) => {
    result[key] = result[key].sort(
      (a: NestedCommission, b: NestedCommission) =>
        (a.rate || 0) - (b.rate || 0)
    );
  });

  // Create a sorted version of the result to ensure groups are ordered by closing date
  const sortedKeys = Object.keys(result).sort((keyA, keyB) => {
    const dateA = keyToEarliestClosingDate[keyA];
    const dateB = keyToEarliestClosingDate[keyB];

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateA.getTime() - dateB.getTime();
  });

  // Create a new object with the sorted keys
  const sortedResult: GroupedCommissions = {};
  for (const key of sortedKeys) {
    sortedResult[key] = result[key];
  }

  // Create sorted entries array to maintain order
  const sortedEntries = sortedKeys.map(
    (key) => [key, sortedResult[key]] as [string, NestedCommission[]]
  );

  return {
    commissionGroups: sortedResult,
    displayNames: keyToDisplayName,
    sortedEntries,
  };
};

// Group commissions by target status (above/below target, tiers) and plan name
export const groupCommissionsByTargetStatus = (
  commissions: NestedCommission[]
): GroupedCommissionsWithDisplayNames => {
  const result: GroupedCommissions = {};
  const keyToDisplayName: Record<string, string> = {};

  // Group commissions by their target status and plan name
  commissions.forEach((commission) => {
    const { accelerator, plan } = commission;
    const { commission_based_on } = accelerator || {};
    const planName = plan?.name || "Unknown Plan";

    if (commission_based_on === "deal_value_above_target") {
      // Group above target commissions by plan name
      const key = `above-target-${plan?.id || 0}`;
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(commission);

      // Set display name to plan name
      keyToDisplayName[key] = planName;
    } else if (commission_based_on === "deal_value_below_target") {
      // Group below target commissions by plan name
      const key = `below-target-${plan?.id || 0}`;
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(commission);

      // Set display name to plan name
      keyToDisplayName[key] = planName;
    } else if (commission_based_on === "deal_value_tier") {
      // Group tiered commissions by plan name
      const key = `target-tier-${plan?.id || 0}`;
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(commission);

      // Set display name to plan name
      keyToDisplayName[key] = planName;
    } else {
      // For commissions that don't have a target status, keep them in their original groups

      // Older method: Don't separate by plan
      // const key =
      //   commission.deals_key ||
      //   (commission.deal
      //     ? `${commission.deal.id}-${abbreviateLongString(commission?.deal?.name, 30)}`
      //     : commission?.plan?.name || "Additional commissions");

      // New method: Keep separate by plan
      const key =
        `${commission.plan?.id || 0}-` +
        (commission.deals_key ||
          (commission.deal
            ? `${commission.deal.id}-${abbreviateLongString(commission.deal.name, 30)}`
            : "additional-commissions"));

      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(commission);

      // Add display name if it doesn't exist
      if (!keyToDisplayName[key] && commission.deal) {
        keyToDisplayName[key] = abbreviateLongString(commission.deal.name, 30);
      } else if (!keyToDisplayName[key]) {
        keyToDisplayName[key] =
          commission?.plan?.name || "Additional commissions";
      }
    }
  });

  // Sort commissions within each group by rate in ascending order
  Object.keys(result).forEach((key) => {
    if (result[key].length > 0) {
      result[key] = result[key].sort(
        (a: NestedCommission, b: NestedCommission) =>
          (a.rate || 0) - (b.rate || 0)
      );
    }
  });

  // Remove empty groups
  const filteredResult: GroupedCommissions = {};
  Object.keys(result).forEach((key) => {
    if (result[key].length > 0) {
      filteredResult[key] = result[key];
    }
  });

  // Create alphabetically sorted entries array
  const sortedKeys = Object.keys(filteredResult).sort((a, b) => {
    return (keyToDisplayName[a] || "").localeCompare(keyToDisplayName[b] || "");
  });

  const sortedEntries = sortedKeys.map(
    (key) => [key, filteredResult[key]] as [string, NestedCommission[]]
  );

  return {
    commissionGroups: filteredResult,
    displayNames: keyToDisplayName,
    sortedEntries,
  };
};

export const groupCommissionsByUserId = (
  commissions: NestedCommission[]
): GroupedCommissions => {
  const grouped: GroupedCommissions = {};

  for (const c of commissions) {
    if (Object.keys(grouped).includes(c.user_id)) {
      grouped[c.user_id] = [...grouped[c.user_id], c];
    } else {
      grouped[c.user_id] = [c];
    }
  }

  return grouped;
};

export const combineEarnedAndProjectedCommissionMonths = (
  earnedCommissionsByMonth: CommissionMonth[],
  projectedCommissionsByMonth: CommissionMonth[]
): CommissionMonthWithProjected[] => {
  const combinedMonths: CommissionMonthWithProjected[] = [];

  // First add all months where commissions are earned
  for (const earnedCommissionMonth of earnedCommissionsByMonth) {
    // FIXME: O(n^2)
    const projectedCommissionForMatchingMonth =
      projectedCommissionsByMonth.find(
        (p) =>
          p.year === earnedCommissionMonth.year &&
          p.month === earnedCommissionMonth.month
      );

    combinedMonths.push({
      ...earnedCommissionMonth,
      projectedValue: projectedCommissionForMatchingMonth?.value ?? 0,
    });
  }

  const alreadyAddedMonths: YearAndMonth[] = combinedMonths.map((e) => ({
    year: e.year,
    month: e.month,
  }));

  // Then catch all the "missed" months from above with no earned and only projected commissions
  // TODO: Probably a better way to do this is to just loop through the months and years
  // of the chosen daterange
  for (const projectedCommissionMonth of projectedCommissionsByMonth) {
    const checkingMonth: YearAndMonth = {
      year: projectedCommissionMonth.year,
      month: projectedCommissionMonth.month,
    };

    if (yearMonthExistsInArray(checkingMonth, alreadyAddedMonths)) {
      continue;
    }

    combinedMonths.push({
      ...projectedCommissionMonth,
      value: 0,
      projectedValue: projectedCommissionMonth.value,
    });
  }

  return combinedMonths;
};

export const isUnapproved = (commissions: NestedCommission[]) => {
  return commissions.some((c) => c.status === "unapproved");
};

export const getUnapproved = (commissions: NestedCommission[]) => {
  return commissions.filter((c) => c.status === "unapproved");
};

export const isIgnored = (commissions: NestedCommission[]) => {
  return commissions.some((c) => c.status === "ignored");
};

export const getIgnored = (commissions: NestedCommission[]) => {
  return commissions.filter((c) => c.status === "ignored");
};

export const getAggregateEarned = (commissions: NestedCommission[]) => {
  return commissions.filter(
    (c) =>
      c.status === "unapproved" ||
      c.status === "approved" ||
      c.status === "paid"
  );
};

export const filterCurrentMonthOnwards = (commissions: NestedCommission[]) => {
  const today = new Date();
  const thisMonth = today.getMonth() + 1; // Commission "month" is JS month + 1
  const thisYear = today.getFullYear();

  return commissions.filter(
    (c) =>
      compareYearMonth(
        { year: c.year, month: c.month },
        { year: thisYear, month: thisMonth }
      ) >= 0
  );
};

export const getDealCount = (commissions: NestedCommission[]) => {
  return new Set(commissions.map((c) => c.deal_id)).size;
};

export const isApproved = (commissions: NestedCommission[]) => {
  return commissions.some((c) => c.status === "approved");
};

export const getUnApproved = (commissions: NestedCommission[]) => {
  return commissions.filter((c) => c.status === "unapproved");
};

export const getApproved = (commissions: NestedCommission[]) => {
  return commissions.filter((c) => c.status === "approved");
};

export const getAggregateApproved = (commissions: NestedCommission[]) => {
  return commissions.filter(
    (c) => c.status === "approved" || c.status === "paid"
  );
};

export const isPaid = (commissions: NestedCommission[]) => {
  return commissions.find((c) => c.status === "paid");
};

export const getPaid = (commissions: NestedCommission[]) => {
  return commissions.filter((c) => c.status === "paid");
};

export const isRejected = (commissions: NestedCommission[]) => {
  return commissions.find((c) => c.status === "rejected");
};

export const getBadgeTitle = (commissions: NestedCommission[]) => {
  if (isUnapproved(commissions))
    return `${getUnapproved(commissions).length} of ${commissions.length
      } needs approval`;
  if (isApproved(commissions))
    return `${getApproved(commissions).length} of ${commissions.length
      } awaiting final approval`;
  if (isPaid(commissions)) return commissionCopy.paid;
};

export const getBadgeVariant = (commissions?: NestedCommission[]) => {
  if (
    !commissions ||
    commissions.length === 0 ||
    getCommissionsValue(commissions) === 0
  )
    return "secondary";
  if (isUnapproved(commissions)) return "unapproved";
  if (isApproved(commissions)) return "approved";
  if (isPaid(commissions)) return "paid";
  if (isRejected(commissions)) return "rejected";
  return "unapproved";
};

export const getBadgeSummary = (commissions?: NestedCommission[]) => {
  if (
    !commissions ||
    commissions.length === 0 ||
    getCommissionsValue(commissions) === 0
  )
    return "N/A";
  if (isUnapproved(commissions)) return commissionCopy.unapproved;
  if (isApproved(commissions)) return commissionCopy.approved;
  if (isPaid(commissions)) return commissionCopy.paid;
  if (isRejected(commissions)) return "Rejected";
  return "N/A";
};

export const updateCommissionsStatus = async (
  commissions: NestedCommission[],
  previousStatus: CommissionStatus,
  newStatus: CommissionStatus,
  filterByCommission?: NestedCommission
) => {
  // Check if this is an above/below target commission
  const isTargetBasedCommission =
    filterByCommission?.accelerator?.commission_based_on ===
    "deal_value_above_target" ||
    filterByCommission?.accelerator?.commission_based_on ===
    "deal_value_below_target";

  // If a specific commission is provided, find all related commissions
  // Otherwise, update all commissions with the given status
  const filteredCommissions = commissions.filter((c) => {
    // Basic check: status must match
    if (c.status !== previousStatus) return false;

    // If no filter commission is provided, include all matching status
    if (!filterByCommission) return true;

    // For above/below target commissions, match by plan ID, user, month, year and target status
    if (isTargetBasedCommission) {
      return (
        c.plan?.id === filterByCommission.plan?.id &&
        c.user_id === filterByCommission.user_id &&
        c.month === filterByCommission.month &&
        c.year === filterByCommission.year &&
        c.accelerator?.commission_based_on ===
        filterByCommission.accelerator?.commission_based_on
      );
    }

    // Fallback to matching by deal name, user, month and year if deal_id is not available
    return (
      c.deal?.name === filterByCommission.deal?.name &&
      c.user_id === filterByCommission.user_id &&
      c.month === filterByCommission.month &&
      c.year === filterByCommission.year
    );
  });

  if (!filteredCommissions.length) return [];
  const updated = filteredCommissions.map(c => ({ ...c, status: newStatus }));
  const isPersisted = newStatus !== "unapproved";
  const isReadOnly = newStatus === "approved" || newStatus === "paid";
  // Keep the existing upsert/delete followed by deal-lock workflow. Complete the
  // lock for every successful save even if another commission in the batch fails.
  const results = await Promise.allSettled(updated.map(async c => {
    if (!c.id) throw new Error("Wait for the commission calculation to finish before approving.");
    const cleaned = {
      ...c, plan: undefined, deal: undefined, accelerator: undefined,
      user: undefined, comment_count: undefined,
    };
    const saved = isPersisted ? await upsertCommission(cleaned) : await deleteCommission(c.id);
    if (!saved) throw new Error("Commission could not be saved.");
    if (c.deal_id) await updateDeal(c.deal_id, { is_read_only: isReadOnly });
    return typeof saved === "object" ? { ...c, ...saved } : c;
  }));
  if (results.some(result => result.status === "rejected")) {
    throw new Error("Some approvals may have been saved. Reload the page before trying again.");
  }
  return results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
};
